import { Card, CircularProgress, Divider, Stack } from "@mui/material";
import {
  getEmailTemplateById,
  updateEmailTemplate,
} from "backend/api/emailing";
import Locker from "components/activities/Locker";
import EmailPreview from "components/emailing/EmailPreview/EmailPreview";
import { EmailTemplateForm } from "components/emailing/EmailTemplate/EmailTemplateForm/EmailTemplateForm";
import { toastEmitter } from "helpers/helpers";
import { useError } from "hooks/useError";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import { NewEmailTemplate } from "types/emailTemplate.type";
import "./UpdateEmailTemplate.css";

export function UpdateEmailTemplate() {
  const [template, setTemplate] = useState<NewEmailTemplate | null>(null);
  const [newTemplate, setNewTemplate] = useState<NewEmailTemplate | null>(null);
  const [isLocked, setIsLocked] = useState(true);

  const { t } = useTranslation("emailing");
  const { id } = useParams();
  const { errorMessage, onError: onFetchError } = useError(
    t,
    "errors.fetchError",
  );
  const { onError: onUpdateError } = useError(t, "emailing.updateError");

  const queryClient = useQueryClient();

  const { isLoading: isLoadingTemplate, isError } = useQuery({
    queryKey: [`email-template-${id}`, "email-templates"],
    queryFn: () => getEmailTemplateById(id as string),
    onSuccess: (data) => {
      if (data) {
        setTemplate({
          ...data,
          attachments: data.attachments.map((attachment) => ({
            url: attachment,
          })),
        });
      }
    },
    onError: onFetchError,
  });

  const { isLoading, mutate: updateTemplate } = useMutation({
    mutationFn: (template: NewEmailTemplate & { id: number }) =>
      updateEmailTemplate(template),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["email-templates"],
      });
      toastEmitter(t("success.templateUpdated"), "success");
    },
    onError: onUpdateError,
  });

  const handleUpdateTemplate = (updatedTemplate: NewEmailTemplate) => {
    if (id) {
      const updatedData = {
        ...updatedTemplate,
        id: Number(id),
      };
      updateTemplate(updatedData);
    }
  };

  return (
    <Card
      sx={{
        position: "relative",
      }}
    >
      {isError && (
        <Stack
          className="container-stack"
          alignItems={"center"}
          justifyContent={"center"}
        >
          {errorMessage}
        </Stack>
      )}
      {!isError && isLoadingTemplate && (
        <Stack
          className="container-stack"
          alignItems={"center"}
          justifyContent={"center"}
        >
          <CircularProgress size={24} />
        </Stack>
      )}
      {!isLoadingTemplate && template && (
        <>
          <Locker isOpen={!isLocked} onClick={() => setIsLocked(!isLocked)} />
          <Stack direction={{ xs: "column", lg: "row" }} spacing={4}>
            <Stack className="container-stack">
              <EmailTemplateForm
                key={template?.id}
                template={template}
                loading={isLoading}
                disabled={isLocked}
                onSubmit={handleUpdateTemplate}
                onChange={setNewTemplate}
              />
            </Stack>
            <Divider
              sx={{
                borderLeftWidth: 1,
              }}
            />
            <Stack className="container-stack">
              {newTemplate && newTemplate.triggerEvent && (
                <EmailPreview template={newTemplate} />
              )}
            </Stack>
          </Stack>
        </>
      )}
    </Card>
  );
}
